import { useState } from 'react'
import { Box, Chip, Grid, Stack, Typography } from '@mui/material'
import {
  THERAPY_DISCIPLINE_NUTRITION_CONSULTATION,
  THERAPY_DISCIPLINE_OPTIONS,
  THERAPY_DISCIPLINE_PSYCHOTHERAPY,
} from 'src/consumer/constants'
import usePractitionerReportInsights from './usePractitionerReportInsights'
import PeriodComparison from './PeriodComparison'
import {
  INSIGHT_COLORS,
  InsightsCard,
  InsightsLoading,
  RatePill,
  SectionEmpty,
  StatTileRow,
  TopKBarChart,
} from './insightsCharts'

function disciplineLabel(code) {
  const found = (THERAPY_DISCIPLINE_OPTIONS || []).find((o) => o?.code === code)
  return found?.label || (code === THERAPY_DISCIPLINE_NUTRITION_CONSULTATION ? 'Nutrition' : 'Psychotherapy')
}

/**
 * Therapist (ROLE_THERAPIST) insights from the practitioner's own TherapyReports. Discipline-aware:
 * psychotherapy shows therapy-type / session-focus / format distributions + practice-quality rates;
 * nutrition shows diet-plan / anthropometry / biochemical capture rates. A practitioner approved for
 * both disciplines gets a toggle (which drives a fresh, lazily-fetched query per discipline).
 */
export default function TherapyInsights({
  active,
  clinicBrandId,
  clinicLocationId,
  from,
  to,
  approvedDisciplineCodes,
  primaryPeriod,
  comparison,
}) {
  const codes =
    approvedDisciplineCodes && approvedDisciplineCodes.length
      ? approvedDisciplineCodes
      : [THERAPY_DISCIPLINE_PSYCHOTHERAPY]
  const [discipline, setDiscipline] = useState(codes[0])
  const showToggle = codes.length > 1

  const { data, loading, error } = usePractitionerReportInsights({
    enabled: active,
    reportType: 'therapy-report',
    discipline,
    clinicBrandId,
    clinicLocationId,
    from,
    to,
  })

  const isNutrition = (data?.discipline || discipline) === THERAPY_DISCIPLINE_NUTRITION_CONSULTATION

  const toggle = showToggle ? (
    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
      {codes.map((code) => (
        <Chip
          key={code}
          label={disciplineLabel(code)}
          onClick={() => setDiscipline(code)}
          color={discipline === code ? 'primary' : 'default'}
          variant={discipline === code ? 'filled' : 'outlined'}
        />
      ))}
    </Stack>
  ) : null

  let body
  if (loading && !data) {
    body = <InsightsLoading />
  } else if (error && !data) {
    body = <SectionEmpty label={error} />
  } else if (!data) {
    body = <SectionEmpty />
  } else {
    const totals = data.totals || {}
    const rates = data.rates || {}
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {comparison ? (
          <PeriodComparison
            active={active}
            reportType="therapy-report"
            discipline={discipline}
            clinicBrandId={clinicBrandId}
            clinicLocationId={clinicLocationId}
            primaryPeriod={primaryPeriod}
            primaryData={data}
            comparison={comparison}
          />
        ) : null}

        <StatTileRow
          tiles={[
            { label: 'Reports', value: totals.reports, color: INSIGHT_COLORS[0] },
            { label: 'Patients', value: totals.patients, color: INSIGHT_COLORS[6] },
          ]}
        />

        {isNutrition ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InsightsCard title="Documentation rates" subtitle="Share of reports capturing each section">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <RatePill label="Diet plan issued" value={rates.dietPlanIssued} />
                  <RatePill label="Anthropometry captured" value={rates.anthropometryCaptured} />
                  <RatePill label="Biochemical markers captured" value={rates.biochemicalCaptured} />
                  <RatePill label="Follow-up given" value={rates.followUpGiven} />
                  <RatePill label="Diagnosis recorded" value={rates.diagnosisRecorded} />
                </Box>
              </InsightsCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <InsightsCard title="Top Diagnoses">
                <TopKBarChart items={data.topDiagnoses} color={INSIGHT_COLORS[0]} emptyLabel="No diagnoses recorded" />
              </InsightsCard>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InsightsCard title="Practice quality" subtitle="Share of sessions with each captured">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <RatePill label="Goals reviewed" value={rates.goalsReviewed} />
                  <RatePill label="Homework given" value={rates.homeworkGiven} />
                  <RatePill label="Risk noted" value={rates.riskNotes} />
                  <RatePill label="Follow-up given" value={rates.followUpGiven} />
                  <RatePill label="Diagnosis recorded" value={rates.diagnosisRecorded} />
                </Box>
              </InsightsCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <InsightsCard title="Therapy Types">
                <TopKBarChart items={data.therapyTypeDistribution} color={INSIGHT_COLORS[0]} emptyLabel="No therapy types recorded" />
              </InsightsCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <InsightsCard title="Top Session Focus">
                <TopKBarChart items={data.topSessionFocus} color={INSIGHT_COLORS[1]} emptyLabel="No session focus recorded" />
              </InsightsCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <InsightsCard title="Session Formats">
                <TopKBarChart items={data.sessionFormatDistribution} color={INSIGHT_COLORS[2]} emptyLabel="No session formats recorded" />
              </InsightsCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <InsightsCard title="Top Diagnoses">
                <TopKBarChart items={data.topDiagnoses} color={INSIGHT_COLORS[3]} emptyLabel="No diagnoses recorded" />
              </InsightsCard>
            </Grid>
          </Grid>
        )}
      </Box>
    )
  }

  return (
    <Box>
      {showToggle ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Discipline
          </Typography>
          {toggle}
        </Box>
      ) : null}
      {body}
    </Box>
  )
}
